/**
 * Один сигнал на браузер при нескольких вкладках §8 (1.20), design.md
 * решение 4: блокировка Web Locks на ключ срабатывания (`alertKey`).
 * `BroadcastChannel` и `localStorage` отвергнуты там же — оба несут гонку
 * между чтением и записью, а `navigator.locks.request` выдаёт заявку
 * атомарно.
 *
 * `claim()` резолвится `true`/`false` сразу по факту заявки — не дожидаясь
 * конца удержания: внутренний `Promise` разрешается из колбэка `request`
 * синхронно в момент его вызова, а возвращённый колбэком промис лишь
 * держит саму блокировку `holdMs` и наружу не протекает.
 */

/** Окно дедупликации одного срабатывания между вкладками — design.md решение 4. */
export const ALERT_DEDUP_WINDOW_MS = 2_000;

/** Минимальная форма `navigator.locks`, которой пользуется этот модуль. */
export interface LockManagerLike {
  request<T>(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown | null) => T | PromiseLike<T>,
  ): Promise<T>;
}

/**
 * Просит блокировку `key` без ожидания в очереди (`ifAvailable: true`).
 * Получила — держит `holdMs`, пока другая заявка на тот же ключ не
 * получает `null` и, соответственно, `false`. Браузер без `navigator.locks`
 * не даёт согласовать вкладки вовсе — тогда заявка всегда `true`
 * (двойной сигнал предпочтительнее пропущенного).
 */
export async function claim(
  key: string,
  holdMs: number,
  locks: LockManagerLike | undefined = globalThis.navigator?.locks,
): Promise<boolean> {
  if (!locks) {
    return true;
  }

  return new Promise<boolean>((resolveClaim) => {
    locks
      .request(key, { ifAvailable: true }, (lock) => {
        if (lock === null) {
          resolveClaim(false);
          return undefined;
        }
        resolveClaim(true);
        return new Promise<void>((resolveHold) => {
          setTimeout(resolveHold, holdMs);
        });
      })
      .catch(() => {
        // Отказ самой заявки (например, окружение оборвало запрос до вызова
        // колбэка) — двойной сигнал предпочтительнее пропущенного, заявка
        // считается разрешённой. Если колбэк успел вызваться и `resolveClaim`
        // уже отдал исход, повторный вызов здесь безвреден: у `Promise`
        // срабатывает только первое разрешение.
        resolveClaim(true);
      });
  });
}
